import type { ReactNode } from 'react'
import { Plus } from 'lucide-react'
import {
  LEVELS, type GraphEdge, type GraphGroup, type GraphModel, type GraphNode,
  type LevelsItem, type LevelsPlacement, type LevelsRect,
} from '@solum/experience'
import { formatDate } from '@solum/format'
import { EntityIcon } from '../EntityIcon'
import { Button } from '../controls/Button'
import { cx } from '../controls/cx'
import s from './RelationshipLevels.module.css'

/**
 * The LEVELS picture (#2406) — the leading design's Connections Graph, drawn
 * from `levelsLayout` and nothing else.
 *
 * `RelationshipGraph` owns the state, the verbs and the accessible tree; this
 * owns only how one placement becomes DOM. It is HTML over an SVG rather than
 * SVG alone because a card here is a CONTROL — it is selected, it names itself
 * to a screen reader and it carries its verbs — and a control drawn as SVG text
 * is a control no browser will focus, wrap or truncate honestly.
 *
 * ── WHAT A CARD SAYS, AND WHERE EACH PART COMES FROM ──────────────────────
 *
 *   name       the governed title, or the platform's one sentence for a record
 *              the caller cannot name — never an id.
 *   subtitle   the door's own subtitle; failing that, the target's registered
 *              label at level 1 (the band already says the relationship) and the
 *              relationship's label at level 2+ (nothing else on screen does).
 *   facts      the placing edge's validity and governed attributes, and only
 *              where it carries some. An empty "Since —" is a field pretending
 *              to be a fact.
 *   status     ONLY where the door stated one: `ended` on an edge-stored
 *              relationship. An FK relationship has no validity, so its card has
 *              no dot — a dot on every card would be decoration, and a dot that
 *              means nothing is worse than none. It is a shape as well as a
 *              colour (filled for current, a ring for ended) and it carries its
 *              word for a screen reader, so the meaning is never colour alone.
 *
 * ── HUE IS THE LEVEL, NOT THE TYPE ─────────────────────────────────────────
 *
 * The leading design tints each relationship TYPE its own hue. This platform has
 * no categorical palette to do that with — `--rel-1..4` are the brand's
 * relationship DEPTH accents, documented as such and used that way by the List
 * mode's nested bands — so here they mean what they mean everywhere else: how
 * many hops out a record is. A type is told apart by its governed icon and its
 * band's label, which is what the design pairs every colour with anyway.
 */
/** One verb. `short` is what a CARD prints; `label` stays its accessible name, and contains it. */
export interface Verb { key: string; label: string; short?: string; run: () => void }

export interface RelationshipLevelsProps {
  placement: LevelsPlacement
  /** The model the placement was made from — hidden groups already removed. */
  model: GraphModel
  /** The FULL model: its edges are what the selected node's facts are read from. */
  full: GraphModel
  selected: string | null
  /** Keys a search did not match. Dimmed, never removed. */
  dimmed: ReadonlySet<string>
  zoom: number
  testId: string
  onSelect: (key: string) => void
  /** The selected node's verbs, built by the owner so every layout offers the same ones. */
  verbsOf: (node: GraphNode) => Verb[]
  iconOf?: (entity: string) => string | null | undefined
  describeFact?: (slot: string, key: string, value: unknown) => { label: string; value: string } | null
  canAdd?: (slot: string) => boolean
  onAdd?: (slot: string) => void
  onShowMore?: (slot: string) => void
  onCollapse?: (node: GraphNode) => void
  nameOf: (node: { label: string | null; entity: string }) => string
  /** The whole picture's one accessible name. */
  label: string
}

type Detail = {
  validFrom?: string | null; validTo?: string | null
  ended?: boolean; facts?: Record<string, unknown>
}

const humanize = (key: string) => key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

/** A placing edge's facts, as a card's one line of them. Pure, so a height can be computed from it. */
export function cardFacts(
  edge: GraphEdge | undefined,
  describeFact?: RelationshipLevelsProps['describeFact'],
): { label: string; value: string }[] {
  if (!edge) return []
  const detail = (edge.detail ?? {}) as Detail
  const out: { label: string; value: string }[] = []
  if (detail.ended && detail.validTo) out.push({ label: 'Ended', value: formatDate(detail.validTo, { withYear: true }) })
  else if (detail.validFrom) out.push({ label: 'Since', value: formatDate(detail.validFrom, { withYear: true }) })
  else if (detail.validTo) out.push({ label: 'Until', value: formatDate(detail.validTo, { withYear: true }) })
  for (const [key, value] of Object.entries(detail.facts ?? {})) {
    if (value == null || value === '') continue
    out.push(describeFact?.(edge.slot, key, value) ?? { label: humanize(key), value: String(value) })
  }
  return out
}

/** The door's statement about a relationship's validity, or nothing where it made none. */
export function cardStatus(edge: GraphEdge | undefined): 'current' | 'ended' | null {
  const ended = (edge?.detail as Detail | undefined)?.ended
  return typeof ended === 'boolean' ? (ended ? 'ended' : 'current') : null
}

/**
 * The height of the SELECTED card's row of verbs, which the layout reserves.
 *
 * The verbs sit IN the card rather than floating beside it. A floating bar was
 * tried first and measured on the fixture: stacked, an expansion's children are
 * drawn directly under their parent, so the bar covered exactly the records the
 * Expand had just brought in. Reserving the row moves what is below the chosen
 * card down by one row — the accordion a person asked for by choosing it — and
 * covers nothing. A coarse pointer gets the 44px floor.
 */
export const verbRowHeight = (coarse: boolean) => (coarse ? 52 : 32)

/** The card height the layout reserves: a line more for facts, a row more for verbs. */
export const cardHeightFor = (facts: number, verbRow = 0) =>
  (facts > 0 ? LEVELS.cardHeight + 18 : LEVELS.cardHeight) + verbRow

const box = (r: LevelsRect, within?: LevelsRect) => ({
  left: r.x - (within?.x ?? 0), top: r.y - (within?.y ?? 0), width: r.width, height: r.height,
})

const countText = (g: GraphGroup) =>
  g.count === null ? null : g.capped ? `at least ${g.count}` : String(g.count)

export function RelationshipLevels({
  placement, model, full, selected, dimmed, zoom, testId, onSelect, verbsOf, iconOf,
  describeFact, canAdd, onAdd, onShowMore, onCollapse, nameOf, label,
}: RelationshipLevelsProps) {
  const edgeByKey = new Map(full.edges.map((e) => [e.key, e] as const))
  const groupOf = new Map(model.groups.map((g) => [g.slot, g] as const))
  const items = [...placement.cards.values(), ...placement.refs]
  const levelOf = (n: number) => String(Math.min(Math.max(n, 1), 4))

  const renderItem = (item: LevelsItem, band: LevelsRect): ReactNode => {
    const node = model.nodes.get(item.key)
    if (!node) return null
    const name = nameOf(node)
    if (item.kind === 'ref') {
      const home = groupOf.get(item.home ?? '')
      // The reference carries ITS relationship's facts: the card elsewhere
      // shows the facts of the edge that placed it, and this is the other one.
      const refFacts = cardFacts(edgeByKey.get(item.via), describeFact)
      return (
        /* A record already drawn in another band, named again here so this
           band's rows agree with its count — and pointing at the card rather
           than becoming a second one. */
        <button key={`ref:${item.via}`} type="button" className={cx(s.ref, dimmed.has(item.key) && s.dim)}
          style={box(item, band)} data-testid={`${testId}-ref`} data-node={item.key}
          data-band={item.band} data-level={item.level}
          aria-pressed={selected === item.key} onClick={() => onSelect(item.key)}>
          <span className={s.refName} data-anonymous={node.label?.trim() ? undefined : true}>{name}</span>
          <span className={s.refWhere}>shown under {home?.label ?? 'another relationship'}</span>
          {refFacts.length > 0 && (
            <span className={s.refFacts} data-testid={`${testId}-ref-facts`}>
              {refFacts.map((f) => `${f.label} ${f.value}`).join(' · ')}
            </span>
          )}
        </button>
      )
    }
    const edge = edgeByKey.get(item.via)
    const facts = cardFacts(edge, describeFact)
    const status = cardStatus(edge)
    const group = groupOf.get(item.band)
    const subtitle = node.subtitle?.trim()
      || (item.level === 1 ? group?.targetLabel : edge?.label) || null
    const isOn = selected === item.key
    const expanded = full.expanded.includes(item.key)
    const nodeVerbs = verbsOf(node)
    return (
      <div key={item.key}
        className={cx(s.card, isOn && s.cardOn, dimmed.has(item.key) && s.dim)}
        style={box(item, band)} data-testid={`${testId}-node`} data-node={item.key}
        data-depth={model.nodes.get(item.key)?.depth ?? item.level} data-level={item.level}
        data-band={item.band} data-selected={isOn || undefined}>
        <button type="button" className={s.cardMain} aria-pressed={isOn}
          data-testid={`${testId}-card`} title={name} onClick={() => onSelect(item.key)}>
          <span className={s.tile} data-level={levelOf(item.level)} aria-hidden="true">
            <EntityIcon iconKey={iconOf?.(node.entity) ?? 'record'} size={14} />
          </span>
          <span className={s.cardName} data-testid={`${testId}-card-name`}
            data-anonymous={node.label?.trim() ? undefined : true}>{name}</span>
          {status && (
            <span className={s.status} data-status={status} data-testid={`${testId}-status`}>
              <span className={s.sr}>{status === 'ended' ? 'Ended' : 'Current'}</span>
            </span>
          )}
          {subtitle && <span className={s.cardSub}>{subtitle}</span>}
          {facts.length > 0 && (
            <span className={s.facts} data-testid={`${testId}-card-facts`}>
              {facts.slice(0, 2).map((f) => (
                <span key={f.label} className={s.fact}>{f.label} <b className="tnum">{f.value}</b></span>
              ))}
            </span>
          )}
        </button>
        {/* THE SELECTED CARD'S VERBS — the canonical group, with the same test
            ids and the same name the other layouts' bar carries, so a person
            (and a test) finds a chosen node's verbs the same way in every
            layout. They follow the card's own button in the DOM, so Tab goes
            from the card straight into them. */}
        {isOn && nodeVerbs.length > 0 && (
          <span className={s.cardVerbs} role="group" data-testid={`${testId}-verbs`}
            aria-label={`Actions for ${name}`}>
            {nodeVerbs.map((verb) => (
              <button key={verb.key} type="button" className={s.cardVerb}
                aria-label={verb.label === (verb.short ?? verb.label) ? undefined : verb.label}
                data-testid={`${testId}-verb-${verb.key}`} onClick={verb.run}>
                {verb.short ?? verb.label}
              </button>
            ))}
          </span>
        )}
        {/* HOVER VERBS, for a pointer only, on every OTHER card. They repeat
            the verbs a card offers once chosen, so they are hidden from
            assistive technology and out of the tab order: a keyboard meets one
            stop per card instead of five, and nothing is reachable here that is
            not reachable there. They cover the card's lower lines while the
            pointer is on it, so no card grows into its neighbour. */}
        {!isOn && nodeVerbs.length > 0 && (
          <span className={s.hoverVerbs} aria-hidden="true" data-testid={`${testId}-card-verbs`}>
            {nodeVerbs.map((verb) => (
              <button key={verb.key} type="button" tabIndex={-1} className={s.cardVerb}
                data-testid={`${testId}-card-verb-${verb.key}`}
                onClick={() => { onSelect(item.key); verb.run() }}>{verb.short ?? verb.label}</button>
            ))}
          </span>
        )}
        {/* The design's collapse knob, on a branch that is open. Pointer-only
            for the same reason as the hover verbs: Collapse is a verb. */}
        {expanded && onCollapse && (
          <button type="button" className={s.knob} tabIndex={-1} aria-hidden="true"
            data-testid={`${testId}-collapse-knob`} title="Collapse"
            onClick={() => onCollapse(node)}>−</button>
        )}
      </div>
    )
  }

  const rootName = nameOf(full.root)
  const types = placement.bands.length
  return (
    <div className={s.stage} role="group" aria-label={label}
      data-testid={`${testId}-stage`} data-compact={placement.compact || undefined}
      style={{ width: placement.bounds.width * zoom, height: placement.bounds.height * zoom }}>
      <div className={s.plane}
        style={{
          width: placement.bounds.width, height: placement.bounds.height,
          transform: zoom === 1 ? undefined : `scale(${zoom})`,
        }}>
        {placement.columns.map((c) => (
          <span key={c.level} className={s.caption} aria-hidden="true" data-testid={`${testId}-caption`}
            style={{ left: c.x, top: LEVELS.pad }}>
            {c.level === 0 ? 'Record' : c.level === 1 ? 'Level 1 · direct' : `Level ${c.level} · nested`}
          </span>
        ))}

        {placement.bands.map((band) => {
          const group = groupOf.get(band.slot)
          if (!group) return null
          const n = countText(group)
          const inBand = items.filter((i) => i.band === band.slot)
          const rest = group.count === null ? null : Math.max(group.count - group.loaded, 0)
          return (
            /* A GROUP, not a <section>: a named section is a landmark, and a
               record with twelve relationship types would hand a screen reader
               twelve landmarks for one picture. */
            <div key={band.slot} className={s.band} style={box(band)} role="group"
              aria-label={n === null ? group.label : `${group.label}, ${n}`}
              data-testid={`${testId}-band`} data-slot={band.slot}>
              <div className={s.bandHead}>
                <span className={s.tile} data-level="1" aria-hidden="true">
                  <EntityIcon iconKey={iconOf?.(group.target) ?? 'record'} size={12} />
                </span>
                <b className={s.bandName} data-testid={`${testId}-band-name`}>{group.label}</b>
                {n !== null && <span className={cx(s.count, 'tnum')} data-testid={`${testId}-band-count`}>{n}</span>}
                {/* Only where the slot OFFERS a write, and the host is the one
                    that knows: the same governed capability the List mode's
                    Add reads, never a button that opens a refusal. */}
                {onAdd && canAdd?.(band.slot) && (
                  <Button type="button" size="sm" className={s.add}
                    data-testid={`${testId}-band-add`}
                    aria-label={`Add to ${group.label}`}
                    onClick={() => onAdd(band.slot)}>
                    <Plus size={12} aria-hidden /> Add
                  </Button>
                )}
              </div>
              {inBand.map((item) => renderItem(item, band))}
              {band.more && (
                <button type="button" className={s.more} style={box(band.more, band)}
                  data-testid={`${testId}-cluster`} data-slot={band.slot}
                  disabled={!onShowMore} onClick={() => onShowMore?.(band.slot)}>
                  {/* The CALLER's count, and a floor where the door capped
                      it. Where it measured nothing there is no number at all. */}
                  {rest === null ? 'Show more' : `Show more · ${group.capped ? 'at least ' : ''}${rest} not loaded yet`}
                </button>
              )}
              {band.empty && (
                <p className={s.empty} style={box(band.empty, band)} data-testid={`${testId}-band-empty`}>
                  {group.count === null ? 'Not counted' : 'None loaded'}
                </p>
              )}
            </div>
          )
        })}

        <svg className={s.wires} width={placement.bounds.width} height={placement.bounds.height}
          aria-hidden="true" focusable="false">
          {placement.wires.map((w) => (
            <path key={w.key} d={w.d} className={w.kind === 'direct' ? s.direct : s.nested}
              data-testid={`${testId}-wire`} data-kind={w.kind} data-from={w.from} data-to={w.to}
              data-slot={w.slot} />
          ))}
        </svg>

        <div className={s.root} style={box(placement.root)}
          data-testid={`${testId}-root`} data-node="root" data-depth={0}>
          <span className={s.rootHead}>
            <span className={s.vein} aria-hidden="true" />
            <b className={s.rootName} data-testid={`${testId}-root-name`}>{rootName}</b>
          </span>
          {full.root.subtitle?.trim() && <span className={s.rootSub}>{full.root.subtitle}</span>}
          <span className={s.rootFoot}>
            <span className="tnum">{model.nodes.size - 1} loaded</span>
            <span aria-hidden="true">·</span>
            <span className="tnum">{types} {types === 1 ? 'type' : 'types'}</span>
          </span>
        </div>
      </div>
    </div>
  )
}
